import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { filter, switchMap, take, timeout } from 'rxjs/operators';
import { BcmApi2Service } from '@modules/bcm/@shared/services';

export interface AppDataShareCreateRequestResult {
    requestUuid: string;
    dataType: 'userProfile' | 'boat';
    sourceUuid: string;
    expiresAt: string;
}

export interface AppDataShareBoatRef {
    id: number;
    name?: string;
    licensePlate?: string;
}

export interface AppDataShareLookupResult {
    outcome: 'notFound' | 'inTenant' | 'needsConsent' | 'kindMismatch';
    dataType?: 'userProfile' | 'boat';
    personId?: number | null;
    boats?: AppDataShareBoatRef[];
}

export interface AppDataShareStatusResult {
    requestUuid: string;
    state: 'pending' | 'approved' | 'denied' | 'expired' | 'cancelled';
    dataType: 'userProfile' | 'boat';
    result_bcm_person_id?: number;
    result_bcm_boat_id?: number;
    boatIds?: number[];
    person?: { id: number; firstName?: string; lastName?: string };
    boat?: AppDataShareBoatRef;
    boats?: AppDataShareBoatRef[];
}

@Injectable({ providedIn: 'root' })
export class BcmAppDataShareApiService extends BcmApi2Service {
    parsePayload(body: { qrPayload?: string; shareCode?: string }) {
        return this.post<{ dataType: 'userProfile' | 'boat'; sourceUuid: string }>('app-data-share/parse', body);
    }

    createRequest(body: {
        qrPayload?: string;
        shareCode?: string;
        importTarget?: 'person' | 'boat';
    }) {
        this.suppressErrorForNextRequest();
        return this.post<AppDataShareCreateRequestResult>('app-data-share/request', body);
    }

    getRequestStatus(requestUuid: string) {
        return this.get<AppDataShareStatusResult>(`app-data-share/request/${requestUuid}/status`);
    }

    cancelRequest(requestUuid: string) {
        return this.post<{ state: string }>(`app-data-share/request/${requestUuid}/cancel`, {});
    }

    lookupShareCode(shareCode: string, importTarget?: 'person' | 'boat') {
        this.suppressErrorForNextRequest();
        return this.post<AppDataShareLookupResult>('app-data-share/lookup', { shareCode, importTarget });
    }

    shareCodeForPerson(personId: number) {
        this.suppressErrorForNextRequest();
        return this.get<{ code: string | null; linked?: boolean }>(`app-data-share/share-code/person/${personId}`);
    }

    shareCodeForBoat(boatId: number) {
        this.suppressErrorForNextRequest();
        return this.get<{ code: string | null; linked?: boolean }>(`app-data-share/share-code/boat/${boatId}`);
    }

    waitForApprovedRequest(requestUuid: string, timeoutMs = 70 * 1000): Observable<AppDataShareStatusResult> {
        return timer(0, 2000).pipe(
            switchMap(() => this.getRequestStatus(requestUuid)),
            filter(status => ['approved', 'denied', 'expired', 'cancelled'].includes(status.state)),
            take(1),
            timeout(timeoutMs)
        );
    }
}
